import React, { useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  FaMapMarkerAlt, FaPaperPlane, FaGlobeAmericas, 
  FaChevronRight, FaCheckCircle, FaStar, FaChevronDown, 
  FaTimesCircle, FaClock, FaBus, FaHotel, FaUtensils, FaShieldAlt,
  FaOm, FaFortAwesome
} from 'react-icons/fa';
import './SacredRoyalRajasthan.css';

const SacredRoyalRajasthan = () => {
  const [openSection, setOpenSection] = useState(null);

  const itineraryRef = useRef(null);
  const highlightsRef = useRef(null);
  const inclusionsRef = useRef(null);
  const aboutRef = useRef(null);

  const scrollToSection = (ref) => {
    window.scrollTo({ top: ref.current.offsetTop - 100, behavior: 'smooth' });
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } }
  };

  const itineraryData = [
    {
      day: 1,
      title: "Arrival in Jaipur & Local Sightseeing",
      points: [
        "Arrival in Jaipur, the 'Pink City', and transfer to the hotel.",
        "After check-in, proceed for sightseeing of the royal heritage.",
        "Visit City Palace, Jantar Mantar Observatory, and the iconic Hawa Mahal.",
        "Evening free to explore the vibrant Jaipur local markets.",
        "Dinner and overnight stay in Jaipur."
      ],
      icon: <FaBus />
    },
    {
      day: 2,
      title: "Jaipur to Khatu Shyam Ji (Divine Darshan)",
      points: [
        "After breakfast, drive to the sacred Khatu Shyam Ji Temple (Approx. 80 km).",
        "Enjoy divine darshan at the temple dedicated to Khatu Shyam Ji (Barbarik).",
        "Experience one of the most revered pilgrimage sites in North India.",
        "After darshan, return to Jaipur.",
        "Dinner and overnight stay in Jaipur."
      ],
      icon: <FaOm />
    },
    {
      day: 3,
      title: "Jaipur to Pushkar (The Holy City)",
      points: [
        "After breakfast, travel to Pushkar (Approx. 150 km | 3 hours).",
        "Visit the Brahma Temple, one of the few temples in the world dedicated to Lord Brahma.",
        "Explore the sacred Pushkar Lake and its holy ghats.",
        "Wander through Pushkar's famous local markets and unique cafés.",
        "Dinner and overnight stay in Pushkar."
      ],
      icon: <FaMapMarkerAlt />
    },
    {
      day: 4,
      title: "Pushkar to Jodhpur (The Blue City)",
      points: [
        "After breakfast, drive towards the historic city of Jodhpur.",
        "Visit the magnificent Mehrangarh Fort, standing 400 feet above the city.",
        "Explore Jaswant Thada (the 'Taj Mahal of Marwar') and the Clock Tower Market.",
        "Enjoy the panoramic views of the blue houses of the old city.",
        "Dinner and overnight stay in Jodhpur."
      ],
      icon: <FaFortAwesome />
    },
    {
      day: 5,
      title: "Jodhpur to Udaipur (via Ranakpur)",
      points: [
        "After breakfast, proceed to Udaipur, the 'City of Lakes'.",
        "En route, visit the world-famous Ranakpur Jain Temple.",
        "Admire the intricate marble carvings and 1,400+ uniquely carved pillars.",
        "Upon arrival in Udaipur, check in to the hotel.",
        "Dinner and overnight stay in Udaipur."
      ],
      icon: <FaGlobeAmericas />
    },
    {
      day: 6,
      title: "Udaipur Full Day Sightseeing",
      points: [
        "After breakfast, explore the major attractions of Udaipur.",
        "Visit the City Palace complex and the sacred Jagdish Temple.",
        "Stroll through Saheliyon Ki Bari (Garden of Maidens) and Fateh Sagar Lake.",
        "Optional: Enjoy a serene sunset boat ride on Lake Pichola.",
        "Dinner and overnight stay in Udaipur."
      ],
      icon: <FaMapMarkerAlt />
    },
    {
      day: 7,
      title: "Departure",
      points: [
        "Enjoy your final Rajasthani breakfast and complete hotel check-out.",
        "Transfer for your onward journey from Udaipur (Airport/Railway Station).",
        "Tour concludes with memorable experiences of Rajasthan's heritage and spiritual landmarks.",
        "Assistance with luggage and drop-off at the departure point."
      ],
      icon: <FaCheckCircle />
    }
  ];

  return (
    <div className="trip-container">
      {/* 1. HEADER */}
      <motion.header initial="hidden" whileInView="visible" viewport={{ once: false }} variants={fadeInUp} className="trip-header">
        <div className="header-left">
          <h1 className="trip-title">Sacred & Royal Rajasthan Tour – <br /> Jaipur, Khatu Shyam Ji, Pushkar, Jodhpur & Udaipur</h1>
          <p className="trip-duration">Duration: 6 nights & 7 days of Royal Heritage</p>
        </div>
        <div className="header-right">
          <div className="info-row"><FaMapMarkerAlt className="header-icon" /> <div className="info-text"><span>Destination:</span><p>Jaipur, Khatu Shyam, Pushkar, Jodhpur, Udaipur</p></div></div>
          <div className="info-row"><FaPaperPlane className="header-icon" /> <div className="info-text"><span>Service Type:</span><p>Heritage & Spiritual Tour</p></div></div>
          <div className="info-row type-tag"><FaGlobeAmericas className="header-icon" /><p>Royal Experience</p></div>
        </div>
      </motion.header>

      {/* 2. IMAGE GRID */}
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: false }} variants={fadeInUp} className="image-grid-container">
        <div className="grid-item large-vertical">
          <img src="https://images.unsplash.com/photo-1706961121783-4ae6c933983a?q=80&w=435&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D" alt="Hawa Mahal Jaipur" />
        </div>
        <div className="grid-item middle-box">
          <img src="https://images.unsplash.com/photo-1715628253914-072d4fc1ee33?q=80&w=387&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D" alt="Udaipur Lake" />
        </div>
        <div className="right-stack">
          <div className="grid-item small-h">
            <img src="https://plus.unsplash.com/premium_photo-1661963217044-50773511aa7e?q=80&w=871&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D" alt="Mehrangarh Fort Jodhpur" />
          </div>
          <div className="grid-item small-h">
            <img src="https://plus.unsplash.com/premium_photo-1691031429919-2273f9603be6?q=80&w=871&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D" alt="Pushkar Ghats" />
          </div>
        </div>
      </motion.div>

      {/* 3. STICKY NAV */}
      <nav className="sticky-nav">
        <button className="tab-link" onClick={() => scrollToSection(itineraryRef)}>Detailed Itinerary</button>
        <button className="tab-link" onClick={() => scrollToSection(highlightsRef)}>Highlights</button>
        <button className="tab-link" onClick={() => scrollToSection(inclusionsRef)}>Policy & Inclusions</button>
        <button className="tab-link" onClick={() => scrollToSection(aboutRef)}>About Trip</button>
      </nav>

      <div className="content-section-wrapper">
        {/* ITINERARY SECTION */}
        <section ref={itineraryRef} className="scroll-section">
          <h2 className="section-main-title">Tour Itinerary</h2>
          <div className="days-list">
            {itineraryData.map((item) => (
              <motion.div key={item.day} initial="hidden" whileInView="visible" viewport={{ once: false, amount: 0.2 }} variants={fadeInUp} className="day-block">
                <div className="day-header">
                  <span className="day-badge">Day {item.day}</span>
                  <h3>{item.title}</h3>
                </div>
                <div className="day-body">
                  {item.points.map((p, i) => (
                    <div key={i} className="point-item">
                      <FaChevronRight className="bullet-icon" />
                      <p>{p}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* HIGHLIGHTS SECTION */}
        <motion.section ref={highlightsRef} initial="hidden" whileInView="visible" viewport={{ once: false }} variants={fadeInUp} className="scroll-section">
          <h2 className="section-main-title">Trip Highlights</h2>
          <div className="highlights-grid">
            <div className="h-item"><FaStar className="star-icon" /> <p>Darshan at the sacred Khatu Shyam Ji Temple</p></div>
            <div className="h-item"><FaStar className="star-icon" /> <p>Visit the famous Brahma Temple in Pushkar</p></div>
            <div className="h-item"><FaStar className="star-icon" /> <p>Explore the royal palaces and Hawa Mahal of Jaipur</p></div>
            <div className="h-item"><FaStar className="star-icon" /> <p>Discover the magnificent Mehrangarh Fort in Jodhpur</p></div>
            <div className="h-item"><FaStar className="star-icon" /> <p>Visit the intricate Ranakpur Jain Temple architecture</p></div>
            <div className="h-item"><FaStar className="star-icon" /> <p>Experience the lakes and palaces of Udaipur – City of Lakes</p></div>
            <div className="h-item"><FaStar className="star-icon" /> <p>Explore traditional Rajasthani markets and local culture</p></div>
          </div>
        </motion.section>

        {/* INCLUSIONS & EXCLUSIONS */}
        <motion.section ref={inclusionsRef} initial="hidden" whileInView="visible" viewport={{ once: false }} variants={fadeInUp} className="scroll-section">
          <div className="policy-box inclusion-box">
            <h4 className="p-title green"><FaCheckCircle /> What's Included</h4>
            <ul className="p-list">
              <li><FaHotel /> Accommodation in comfortable hotels as per itinerary</li>
              <li><FaClock /> 6 Nights stay in heritage/3-star category hotels</li>
              <li><FaUtensils /> Meal Plan: Daily Breakfast and Dinner at the hotel</li>
              <li><FaBus /> Transportation in a private A/C vehicle for the entire journey</li>
              <li><FaMapMarkerAlt /> All sightseeing as mentioned in the itinerary</li>
              <li><FaCheckCircle /> Driver allowance, toll taxes, parking, and fuel costs</li>
              <li><FaShieldAlt /> Tour coordination and on-trip assistance by Chandrama Holidays</li>
            </ul>
          </div>
          <div className="policy-box exclusion-box">
            <h4 className="p-title red"><FaTimesCircle /> What's Excluded</h4>
            <ul className="p-list">
              <li><FaTimesCircle /> Airfare or train tickets for arrival/departure</li>
              <li><FaTimesCircle /> Entry tickets to monuments, forts, and temples</li>
              <li><FaTimesCircle /> Jeep ride at Amber Fort or Boat ride at Lake Pichola</li>
              <li><FaTimesCircle /> Personal expenses (Shopping, Laundry, Room Service, etc.)</li>
              <li><FaUtensils /> Lunch or any meals not explicitly mentioned</li>
              <li><FaShieldAlt /> Travel insurance or medical expenses</li>
              <li><FaTimesCircle /> Any cost arising due to unforeseen weather or road blocks</li>
            </ul>
          </div>
        </motion.section>

        {/* ABOUT SECTION */}
        <motion.section ref={aboutRef} initial="hidden" whileInView="visible" viewport={{ once: false }} variants={fadeInUp} className="scroll-section">
          <h2 className="section-main-title">About This Journey</h2>
          <p className="about-text">
            The Sacred & Royal Rajasthan Tour blends the magnificent heritage of Rajasthan with visits to some of its most revered spiritual destinations. This journey covers Jaipur, Khatu Shyam Ji, Pushkar, Jodhpur, and Udaipur, offering travelers a unique combination of devotion, culture, and architectural beauty.
            <br /><br />
            Travelers will explore majestic forts and palaces, visit sacred temples including the famous Khatu Shyam Ji Temple and Brahma Temple in Pushkar, and experience the serene lakes and royal heritage of Udaipur. With balanced sightseeing and spiritual experiences, this journey provides a meaningful exploration of Rajasthan’s cultural and religious traditions.
          </p>
        </motion.section>

        {/* FOOTER LINKS */}
        <div className="accordion-item">
          <Link to="/terms-conditions" className="accordion-header" style={{ textDecoration: 'none', color: 'inherit' }}>
            <span>Terms & Conditions</span>
            <FaChevronDown className="arrow-icon" style={{ transform: 'rotate(-90deg)' }} /> 
          </Link>
        </div>

        <div className="accordion-item">
          <Link to="/privacy-policy" className="accordion-header" style={{ textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}>
            <span>Privacy Policy</span>
            <FaChevronDown className="arrow-icon" style={{ transform: 'rotate(-90deg)' }} />
          </Link>
        </div>

        {/* IMPORTANT NOTE */}
        <motion.section initial="hidden" whileInView="visible" viewport={{ once: false }} variants={fadeInUp} className="scroll-section travel-info-grid">
          <div className="info-card note-card" style={{ width: '100%' }}>
            <h4 className="info-card-title"><FaPaperPlane /> Important Note</h4>
            <p>Flight and train tickets can be arranged by <strong>Chandrama Holidays</strong> on request at the best available fares. Our team will assist you in planning convenient travel arrangements for a smooth journey.</p>
          </div>
        </motion.section>
      </div>
    </div>
  );
};

export default SacredRoyalRajasthan;